import React, { useState, useEffect } from 'react';
import '../../App.css';
import { Link, useNavigate } from 'react-router-dom'; // Import Link and useNavigate components
import { fetchDashboardData, setTracking } from '../../services/api'; // Import your functions
import { SideMenu, TopMenu } from 'maxcu-shared-lib';
import { Header } from 'maxcu-shared-lib';
import menuItems from '../SideMenu/menuItems';

const styles = {
  heading: {
    fontSize: '2rem',
    textAlign: 'center',
    marginBottom: '1rem'
  },
  paragraph: {
    textAlign: 'center',
    fontSize: '1.2rem',
    lineHeight: '1.2',
    marginBottom: '2rem'
  }
};

// Add this at the top of your file, after the imports
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);

  return matches;
};

function App() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [amountToSave, setAmountToSave] = useState('');
  const [goal, setGoal] = useState('');
  const [saveType, setSaveType] = useState('2');
  const [payment, setPayment] = useState('');
  const [save, setSave] = useState('');
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 600px)');

  useEffect(() => {
    document.title = 'Round Up | MAX Credit Union';
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const jsonData = await fetchDashboardData();
        setData(jsonData);
      } catch (error) {
        setError(error.message);
      }
    }
    fetchData();
  }, []);

  if (error) return <div>Error: {error}</div>;
  if (!data) return <div>Loading...</div>;

  const handleSubmit = async() => {
    try {
      const formattedAmount = parseFloat(amountToSave).toFixed(2);
      
      const trackingData = {
        amountToSave: formattedAmount,
        goal: goal.trim(),
        saveType: parseInt(saveType, 10),
        payment: payment.trim(),
        save: save.trim()
      };

      if (!trackingData.amountToSave || isNaN(trackingData.amountToSave)) {
        throw new Error('Please enter a valid savings amount');
      }
      if (!trackingData.goal) {
        throw new Error('Please enter a goal title');
      }
      if (!trackingData.payment) {
        throw new Error('Please select a payment account');
      }
      if (!trackingData.save) {
        throw new Error('Please select a savings account');
      }

      await setTracking(trackingData);
      navigate('/Dashboard');
    } catch (error) {
      setError(error.message || 'Failed to submit form. Please try again.');
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      width: '100vw',
      margin: 0,
      padding: 0,
      overflowX: 'hidden'
    }}>
      <div style={{ width: '100%' }}>
        <Header name='Round Up' /> 
        <SideMenu menuItems={menuItems} />
        {isMobile && <TopMenu />}
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flex: 1,
        paddingTop: '20px',
        textAlign: 'center',
        
        transform: isMobile ? 'scale(0.99)' : 'none',
        transformOrigin: 'top center'
      }}>
        <h2 style={{ fontSize: '1.0rem', textAlign: 'center', marginTop: 0, marginBottom: '0.5rem' }}>PUT YOUR SAVINGS ON AUTOPILOT</h2>
        
        <p style={{ fontSize: '1rem', lineHeight: '1.2', marginBottom: '0.1rem' }}>
          Round Up is an easy way to make everyday purchases part of your savings plan.
        </p>
        <p style={{ fontSize: '1rem', lineHeight: '1.2', marginTop: 0, marginBottom: '0.5rem' }}>
          Quickly set it, forget it, and let your savings grow.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
          <img src="/Savings01.png" alt="Savings Piggy" style={{ width: '30%', height: 'auto' }} />
        </div>

        <div style={{ display: 'grid', gap: '0.5rem', maxWidth: '500px', margin: '0 auto' }}>
          <div className="input-group" style={{ marginBottom: '0.3rem', position: 'relative' }}>
            <label htmlFor="amountToSave" style={{ fontWeight: 'bold', marginBottom: '0.2rem' }}>Set Savings Goal:</label>
            <div style={{ position: 'relative' }}>
              <span style={{ 
                position: 'absolute', 
                left: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#666'
              }}>$</span>
              <input
                type="text"
                id="amountToSave"
                name="amountToSave"
                placeholder="0.00"
                required
                value={amountToSave}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow numbers and one decimal point
                  if (/^\d*\.?\d{0,2}$/.test(value)) {
                    setAmountToSave(value);
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value) {
                    const num = parseFloat(e.target.value);
                    if (!isNaN(num)) {
                      setAmountToSave(num.toFixed(2));
                    }
                  }
                }}
                style={{
                  width: '90%',
                  padding: '8px 8px 8px 25px',
                  borderRadius: '5px',
                  border: '1px solid #bdc3c7',
                  lineHeight: '1.2'
                }}
              />
            </div>
          </div>

          <div className="input-group" style={{ marginBottom: '0.3rem' }}>
            <label htmlFor="goal" style={{ fontWeight: 'bold', marginBottom: '0.2rem' }}>Goal Title:</label>
            <input
              type="text"
              id="goal"
              name="goal"
              placeholder="Enter your savings goal"
              required
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              style={{
                width: '95%',
                padding: '8px',
                borderRadius: '5px',
                border: '1px solid #bdc3c7',
                lineHeight: '1.2'
              }}
            />
          </div>

          <div className="method-to-save" style={{ marginBottom: '0.3rem' }}>
            <label htmlFor="SaveType" style={{ fontWeight: 'bold', marginBottom: '0.2rem' }}>Set Round Up Amount Per Purchase</label>
            <select
              id="SaveType"
              name="SaveType"
              required
              value={saveType}
              onChange={(e) => setSaveType(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '5px',
                border: '1px solid #bdc3c7',
                lineHeight: '1.2'
              }}
            >
              <option value="2">Round up to next dollar</option>
              <option value="1">1% of purchase total</option>
              <option value="3">3% of purchase total</option>
            </select>
          </div>
          <div className="input-group" style={{ marginBottom: '0.3rem' }}>
            <label htmlFor="payment" style={{ fontWeight: 'bold', marginBottom: '0.2rem' }}>Round Up from This Account</label>
            <select
              id="payment"
              name="payment"
              required
              value={payment}
              onChange={(e) => setPayment(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '5px',
                border: '1px solid #bdc3c7',
                lineHeight: '1.2'
              }}
            >
              {data.SHARE.filter(share => share.SHARETYPE.trim() === '1').map((share, index) => {
                // Set default value for payment if it's empty and this is the first item
                if (payment === '' && index === 0) {
                  setTimeout(() => setPayment(share.SHAREID), 0);
                }
                return (
                  <option key={share.SHAREID} value={share.SHAREID}>
                    {share.SHAREID} {share.SHAREDESC} ${parseFloat(share.SHAREBAL.trim()).toFixed(2)}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="input-group" style={{ marginBottom: '0.3rem' }}>
            <label htmlFor="save" style={{ fontWeight: 'bold', marginBottom: '0.2rem' }}>Deposit Round Up Savings in This Account</label>
            <select
              id="save"
              name="save"
              required
              value={save}
              onChange={(e) => setSave(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '5px',
                border: '1px solid #bdc3c7',
                lineHeight: '1.2'
              }}
            >
              {data.SHARE.filter(share => share.SHARETYPE.trim() === '0').map((share, index) => {
                // Set default value for save if it's empty and this is the first item
                if (save === '' && index === 0) {
                  setTimeout(() => setSave(share.SHAREID), 0);
                }
                return (
                  <option key={share.SHAREID} value={share.SHAREID}>
                    {share.SHAREID} {share.SHAREDESC} ${parseFloat(share.SHAREBAL.trim()).toFixed(2)}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <button 
          onClick={handleSubmit}
          style={{
            padding: '10px 20px',
            fontSize: '1.2rem',
            backgroundColor: '#1e3a8a',
            color: 'white',
            border: 'blue',
            borderRadius: '5px',
            cursor: 'pointer',
            lineHeight: '1.2'
          }}
        >
          Set Goal
        </button>
      </div>
    </div>
  );
}

export default App;